<template>
    <div class="blog-admin" v-if="currentUser">
        <div class="page-header">
            <h1>Zarządzanie Blogiem</h1>
            <router-link to="/admin-profile/blog/create" class="btn-primary">+ Dodaj wpis</router-link>
        </div>

        <div class="blog-layout">
            <!-- Posts section -->
            <div class="posts-section">
                <div class="section-card">
                    <div class="section-card-header">
                        <h2>Lista wpisów</h2>
                        <div class="search-bar">
                            <input
                                type="text"
                                placeholder="Wyszukaj wpisy..."
                                v-model="searchEntryInput"
                                @keyup.enter="searchEntries"
                            >
                            <button @click="searchEntries" class="btn-green">Szukaj</button>
                            <button @click="resetSearch" class="btn-outline">Resetuj</button>
                        </div>
                    </div>

                    <div class="posts-list">
                        <div class="post-card" v-for="entry in entries.entries" :key="entry.id">
                            <div class="post-card-image">
                                <img :src="entry.photo_url" :alt="entry.title">
                            </div>
                            <div class="post-card-body">
                                <div class="post-card-meta">
                                    <span class="post-category">{{ entry.category.title }}</span>
                                    <span class="post-target">{{ entry.target }}</span>
                                </div>
                                <h3 class="post-title">{{ entry.title }}</h3>
                                <p class="post-desc">{{ entry.short_description }}</p>
                                <div class="post-card-footer">
                                    <span class="post-author">Autor: {{ entry.author }}</span>
                                    <div class="post-actions">
                                        <label class="popular-toggle" :for="`popular-${entry.id}`">
                                            <input
                                                type="checkbox"
                                                :id="`popular-${entry.id}`"
                                                :checked="entry.popular"
                                                @change="setIsPopular(entry.id, !entry.popular)"
                                            >
                                            <span>Popularny</span>
                                        </label>
                                        <router-link
                                            :to="`/admin-profile/blog/edit/${entry.id}`"
                                            class="btn-icon"
                                            title="Edytuj"
                                        >
                                            <img src="@/assets/icons/pencil-square.svg" alt="edytuj">
                                        </router-link>
                                        <button
                                            class="btn-icon btn-danger"
                                            @click="deleteEntry(entry.id)"
                                            title="Usuń"
                                        >
                                            <img src="@/assets/icons/trash.svg" alt="usuń">
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="entries.entries.length === 0" class="empty-state">
                            <p>Brak wpisów do wyświetlenia.</p>
                        </div>
                    </div>

                    <div class="pagination">
                        <button @click="getEntriesData('-1')" class="btn-outline">&#8592; Poprzednia</button>
                        <span class="page-info">Strona {{ page }}</span>
                        <button @click="getEntriesData('+1')" class="btn-outline">Następna &#8594;</button>
                    </div>
                </div>
            </div>

            <!-- Categories section -->
            <div class="categories-section">
                <div class="section-card">
                    <h2>Kategorie</h2>
                    <div class="categories-list">
                        <div class="category-item" v-for="cat in categories.categoriesList" :key="cat.id">
                            <div class="category-info">
                                <span class="category-title">{{ cat.title }}</span>
                                <span class="category-desc">{{ cat.short_description }}</span>
                            </div>
                            <div class="category-actions">
                                <button class="btn-icon" @click="setupCategoryEdit(cat.id, cat.title, cat.short_description)">
                                    <img src="@/assets/icons/pencil-square.svg" alt="edytuj">
                                </button>
                                <button class="btn-icon btn-danger" @click="deleteCategory(cat.id)">
                                    <img src="@/assets/icons/trash.svg" alt="usuń">
                                </button>
                            </div>
                        </div>
                        <div v-if="categories.categoriesList.length === 0" class="empty-state">
                            <p>Brak kategorii.</p>
                        </div>
                    </div>

                    <button @click="toggleCategoryForm" class="btn-primary cat-add-btn">
                        {{ categories.isCategoryAdding ? 'Zamknij' : '+ Dodaj kategorię' }}
                    </button>

                    <div v-if="categories.isCategoryAdding" class="category-form">
                        <label>Tytuł kategorii</label>
                        <input type="text" v-model="categories.newCategoryTitle" placeholder="Tytuł kategorii">
                        <label>Opis kategorii</label>
                        <textarea v-model="categories.newCategoryDesc" placeholder="Opis kategorii" rows="3"></textarea>
                        <button @click="editAddCategory" class="btn-green">
                            {{ categories.newCategoryId ? 'Zapisz zmiany' : 'Dodaj' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import authHeader from '@/services/auth-header';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

export default {
    name: 'BlogEdit',
    setup() {
        return {
            authStore: useAuthStore(),
            appStore: useAppStore(),
        };
    },
    data() {
        return {
            page: 1,
            categories: {
                newCategoryDesc: '',
                newCategoryTitle: '',
                isCategoryAdding: false,
                categoriesList: [],
                newCategoryId: undefined,
            },
            entries: {
                entries: [],
            },
            searchEntryInput: '',
        };
    },
    computed: {
        currentUser() {
            return this.authStore.user;
        },
    },
    mounted() {
        if (!this.currentUser) {
            this.$router.push('/login');
        } else if (this.currentUser.role_id !== 1) {
            this.$router.push('/login');
        }
        this.getCategories();
        this.getEntriesData('0');
    },
    methods: {
        getCategories() {
            axios.post('/BlogCategorySearch', { page: 0, limit: 50 })
                .then(res => {
                    this.categories.categoriesList = res.data.items;
                });
        },
        deleteCategory(id) {
            axios.delete(`/BlogCategory/${id}`, authHeader())
                .then(res => {
                    if (String(res.status).charAt(0) === '2') {
                        this.getCategories();
                    }
                });
        },
        editAddCategory() {
            if (this.categories.newCategoryTitle !== '' && this.categories.newCategoryDesc !== '') {
                const ob = {
                    title: this.categories.newCategoryTitle,
                    short_description: this.categories.newCategoryDesc,
                };
                if (this.categories.newCategoryId !== undefined) {
                    ob.id = this.categories.newCategoryId;
                }
                axios.post('/BlogCategory', ob, authHeader())
                    .then(res => {
                        if (String(res.status).charAt(0) === '2') {
                            this.getCategories();
                            this.categories.isCategoryAdding = false;
                            this.categories.newCategoryId = undefined;
                            this.categories.newCategoryTitle = '';
                            this.categories.newCategoryDesc = '';
                        }
                    });
            }
        },
        setupCategoryEdit(id, title, desc) {
            this.categories.isCategoryAdding = true;
            this.categories.newCategoryId = id;
            this.categories.newCategoryTitle = title;
            this.categories.newCategoryDesc = desc;
        },
        toggleCategoryForm() {
            this.categories.isCategoryAdding = !this.categories.isCategoryAdding;
            if (!this.categories.isCategoryAdding) {
                this.categories.newCategoryId = undefined;
                this.categories.newCategoryTitle = '';
                this.categories.newCategoryDesc = '';
            }
        },
        getEntriesData(value) {
            switch (value) {
                case '-1':
                    if (this.page > 1) this.page -= 1;
                    break;
                case '0':
                    this.page = 1;
                    break;
                case '+1':
                    this.page += 1;
                    break;
            }
            axios.post('/BlogPostSearch', { page: this.page, limit: 6 }, authHeader())
                .then(res => {
                    this.entries.entries = res.data.items;
                });
        },
        deleteEntry(id) {
            axios.delete(`/BlogPost/${id}`, authHeader())
                .then(res => {
                    if (String(res.status).charAt(0) === '2') {
                        this.appStore.setIsSuccess('Wpis został usunięty');
                        this.getEntriesData('0');
                    }
                });
        },
        searchEntries() {
            if (this.searchEntryInput.length > 0) {
                axios.post('/BlogPostSearch', { page: 1, limit: 1000 }, authHeader())
                    .then(res => {
                        this.entries.entries = res.data.items.filter(
                            entry => entry.title.toLowerCase().includes(this.searchEntryInput.toLowerCase())
                        );
                    })
                    .catch(() => {});
            } else {
                this.appStore.setIsAlert('Wpisz co najmniej 1 znak');
            }
        },
        resetSearch() {
            this.searchEntryInput = '';
            this.getEntriesData('0');
        },
        setIsPopular(id, status) {
            if (id) {
                axios.post('/SetIsPopular', { post_id: id, is_popular: status }, authHeader())
                    .then(res => {
                        if (String(res.status).charAt(0) === '2') {
                            this.appStore.setIsSuccess(status ? 'Wpis oznaczony jako popularny' : 'Wpis nie jest już popularny');
                            this.getEntriesData('0');
                        }
                    });
            }
        },
    },
};
</script>

<style scoped>
.blog-admin {
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 16px;
    font-family: sans-serif;
    box-sizing: border-box;
}

.page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 32px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
    font-size: 2.8rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.blog-layout {
    display: flex;
    gap: 24px;
    align-items: flex-start;
}

.posts-section {
    flex: 1 1 0;
    min-width: 0;
}

.categories-section {
    width: 300px;
    flex-shrink: 0;
}

@media (max-width: 900px) {
    .blog-layout {
        flex-direction: column;
    }
    .categories-section {
        width: 100%;
    }
}

.section-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.08);
    padding: 24px;
    box-sizing: border-box;
    width: 100%;
}

.section-card h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 16px 0;
}

.section-card-header {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
}

.search-bar {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.search-bar input {
    flex: 1 1 160px;
    min-width: 0;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 8px 14px;
    font-size: 1.5rem;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
}

.search-bar input:focus {
    border-color: #10b981;
}

/* Buttons */
.btn-primary {
    background-color: #f97316;
    color: #fff;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    transition: background-color 0.2s;
}

.btn-primary:hover {
    background-color: #ea6c0a;
}

.btn-green {
    background-color: #10b981;
    color: #fff;
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
}

.btn-green:hover {
    background-color: #059669;
}

.btn-outline {
    background-color: transparent;
    color: #1f2937;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    font-size: 1.5rem;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
    border-color: #10b981;
    color: #10b981;
}

.btn-icon {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    transition: background-color 0.2s;
    text-decoration: none;
}

.btn-icon:hover {
    background-color: #f3f4f6;
}

.btn-icon img {
    width: 20px;
    height: 20px;
}

.btn-danger:hover {
    background-color: #fee2e2;
}

/* Post cards */
.posts-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.post-card {
    display: flex;
    gap: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    transition: box-shadow 0.2s, border-color 0.2s;
}

.post-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    border-color: #10b981;
}

.post-card-image {
    width: 160px;
    flex-shrink: 0;
}

.post-card-image img {
    width: 160px;
    height: 100%;
    min-height: 120px;
    object-fit: cover;
    display: block;
}

.post-card-body {
    flex: 1;
    padding: 14px 16px 14px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.post-card-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.post-category {
    background-color: #d1fae5;
    color: #065f46;
    font-size: 1.2rem;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.post-target {
    background-color: #ffedd5;
    color: #9a3412;
    font-size: 1.2rem;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
}

.post-title {
    font-size: 1.7rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.post-desc {
    font-size: 1.4rem;
    color: #6b7280;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.post-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
    flex-wrap: wrap;
    gap: 8px;
}

.post-author {
    font-size: 1.3rem;
    color: #9ca3af;
}

.post-actions {
    display: flex;
    align-items: center;
    gap: 4px;
}

.popular-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 1.3rem;
    color: #6b7280;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
}

.popular-toggle:hover {
    background-color: #f3f4f6;
}

.popular-toggle input[type="checkbox"] {
    accent-color: #10b981;
    width: 16px;
    height: 16px;
    cursor: pointer;
}

@media (max-width: 600px) {
    .post-card {
        flex-direction: column;
    }
    .post-card-image {
        width: 100%;
    }
    .post-card-image img {
        width: 100%;
        height: 180px;
        min-height: unset;
    }
    .post-card-body {
        padding: 12px;
    }
    .post-card-footer {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (max-width: 480px) {
    .blog-admin {
        padding: 16px 12px;
    }
    .page-header {
        margin-bottom: 20px;
    }
    .page-header h1 {
        font-size: 2.2rem;
    }
    .section-card {
        padding: 16px 12px;
        border-radius: 8px;
    }
    .section-card h2 {
        font-size: 1.8rem;
    }
    .search-bar {
        flex-direction: column;
    }
    .search-bar input {
        width: 100%;
    }
    .btn-green,
    .btn-outline {
        width: 100%;
        text-align: center;
    }
    .pagination {
        gap: 8px;
    }
    .pagination .btn-outline {
        padding: 8px 10px;
        font-size: 1.3rem;
        width: auto;
    }
}

/* Pagination */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
}

.page-info {
    font-size: 1.5rem;
    color: #6b7280;
    font-weight: 500;
}

/* Categories */
.categories-list {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.category-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #e5e7eb;
    gap: 8px;
}

.category-item:last-child {
    border-bottom: none;
}

.category-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}

.category-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
}

.category-desc {
    font-size: 1.3rem;
    color: #9ca3af;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.category-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}

.cat-add-btn {
    width: 100%;
    margin-top: 16px;
    text-align: center;
}

.category-form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
}

.category-form label {
    font-size: 1.4rem;
    font-weight: 600;
    color: #374151;
}

.category-form input,
.category-form textarea {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 1.4rem;
    outline: none;
    transition: border-color 0.2s;
    resize: vertical;
}

.category-form input:focus,
.category-form textarea:focus {
    border-color: #10b981;
}

/* Empty state */
.empty-state {
    text-align: center;
    padding: 32px;
    color: #9ca3af;
    font-size: 1.5rem;
}
</style>
